import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** A scheduled window belonging to one operation, drawn as coloured bands. */
export interface ScheduleBand {
  days: string[];
  startTime: string | null;
  endTime: string | null;
  /** CSS colour for this band (e.g. a token var()). Defaults to brand. */
  color?: string;
  /** Fade the band (sleep windows render dimmer). */
  sleep?: boolean;
}

/** One rendered bar within a day column. */
interface RenderBar {
  top: number;
  height: number;
  color: string;
  opacity: number;
}
interface DayColumn {
  key: string;
  label: string;
  active: boolean;
  bars: RenderBar[];
}

const WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

/** Normalise a day code to its 3-letter uppercase form ('MONDAY' → 'MON'). */
function dayKey(d: string): string {
  return d.slice(0, 3).toUpperCase();
}

function toHours(time: string | null | undefined): number | null {
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h)) return null;
  return h + (m || 0) / 60;
}

/** Window → 0–100% spans, split into two if it wraps past midnight. */
function windowSpans(startTime: string | null, endTime: string | null): { top: number; height: number }[] {
  const s = toHours(startTime);
  const e = toHours(endTime);
  if (s === null || e === null) return [];
  const span = (from: number, to: number) => ({ top: (from / 24) * 100, height: ((to - from) / 24) * 100 });
  return e > s ? [span(s, e)] : [span(0, e), span(s, 24)];
}

/**
 * Weekly schedule strip — 7 day columns, each a 0–24h track with active windows
 * drawn as vertical bands. Two modes:
 *
 * - Single schedule (`days`/`startTime`/`endTime`): one operation's window.
 * - Multi-operation (`bands`): overlays each operation's window in its own
 *   colour over a grey "profile defaults, always-on" base — the design's
 *   "weekly coverage from operations".
 *
 *   <app-schedule-strip [days]="op.days" startTime="05:00" endTime="23:00" />
 *   <app-schedule-strip [bands]="opBands" [coverageBase]="true" [axis]="true" />
 */
@Component({
  selector: 'app-schedule-strip',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    /* Coverage base — the design's dark-grey "running on profile defaults"
       track (surface-4: greyscale-700 light / 400 dark, no app utility). */
    .cov-base {
      background: var(--core-greyscale-700);
    }
    :host-context(.dark) .cov-base,
    :host-context([data-theme='dark']) .cov-base {
      background: var(--core-greyscale-400);
    }
  `,
  template: `
    <div class="flex items-stretch gap-2.5">
      @if (axis()) {
        <div class="relative w-9 flex-none" [style.height.px]="height()">
          @for (tick of ticks; track tick) {
            <div
              class="absolute right-0 -translate-y-1/2 font-num text-[9px] font-semibold text-ink-3"
              [style.top.%]="(tick / 24) * 100"
            >
              {{ tick < 10 ? '0' + tick : tick }}:00
            </div>
          }
        </div>
      }
      <div class="flex flex-1 items-end gap-1.5">
        @for (col of columns(); track col.key) {
          <div class="flex min-w-0 flex-1 flex-col items-center gap-1.5">
            <div
              class="relative w-full overflow-hidden shadow-hairline"
              [class.cov-base]="coverageBase()"
              [class.bg-surface-2]="!coverageBase()"
              [style.height.px]="height()"
              [style.opacity]="col.active || coverageBase() ? 1 : 0.5"
            >
              @for (bar of col.bars; track $index) {
                <div
                  class="absolute inset-x-0"
                  [style.top.%]="bar.top"
                  [style.height.%]="bar.height"
                  [style.background]="bar.color"
                  [style.opacity]="bar.opacity"
                ></div>
              }
            </div>
            @if (!hideLabels()) {
              <div
                class="text-[10px] font-bold tracking-wide"
                [class.text-ink-2]="col.active || coverageBase()"
                [class.text-ink-3]="!col.active && !coverageBase()"
              >
                {{ col.label }}
              </div>
            }
          </div>
        }
      </div>
    </div>
  `,
})
export class ScheduleStrip {
  // Single-schedule inputs.
  readonly days = input<string[]>([]);
  readonly startTime = input<string | null>(null);
  readonly endTime = input<string | null>(null);
  readonly sleep = input(false);
  readonly shutdown = input(false);

  /** Multi-operation overlay; when set, takes precedence over the single inputs. */
  readonly bands = input<ScheduleBand[]>([]);
  /** Paint the track grey ("profile defaults always-on") behind the bands. */
  readonly coverageBase = input(false);

  readonly height = input(76);
  readonly axis = input(false);
  readonly hideLabels = input(false);

  protected readonly ticks = [0, 6, 12, 18, 24];

  /** Normalised bands: multi-op if provided, else the single schedule as one band. */
  private readonly effectiveBands = computed<Required<ScheduleBand>[]>(() => {
    const list =
      this.bands().length > 0
        ? this.bands()
        : [
            {
              days: this.days(),
              startTime: this.startTime(),
              endTime: this.endTime(),
              sleep: this.sleep(),
              color: this.shutdown() ? 'var(--color-warning)' : 'var(--color-brand)',
            },
          ];
    return list.map((b) => ({
      days: b.days ?? [],
      startTime: b.startTime ?? null,
      endTime: b.endTime ?? null,
      color: b.color ?? 'var(--color-brand)',
      sleep: b.sleep ?? false,
    }));
  });

  protected readonly columns = computed<DayColumn[]>(() => {
    const bands = this.effectiveBands();
    return WEEK.map((code) => {
      const bars: RenderBar[] = [];
      let active = false;
      for (const b of bands) {
        if (!b.days.some((d) => dayKey(d) === code)) continue;
        active = true;
        for (const span of windowSpans(b.startTime, b.endTime)) {
          bars.push({ ...span, color: b.color, opacity: b.sleep ? 0.55 : 0.92 });
        }
      }
      return { key: code, label: code[0] + code.slice(1).toLowerCase(), active, bars };
    });
  });
}
